import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { SubscriptionStatus } from "@common/billing";
import { OrganizationUserRole } from "@common/userOrganizationRoles";
import {
    isClientBillingTransferBlockedPlan,
    isRestrictedOrganizationClientPlan,
    isTaxPlanChangeTargetPlan,
} from "@common/constants/organization-billing";
import { ValidateResult, hasAtLeastOrganizationRole } from "./lib";

export function validateManageClient(
    clientId: string,
    actingUserRole: OrganizationUserRole,
    actingUserWorkspaceAssignments: string[],
) {
    if (
        !hasAtLeastOrganizationRole(
            actingUserRole,
            OrganizationUserRole.MANAGER,
        )
    ) {
        return "Only Managers and Admins can update Client settings";
    }

    if (!actingUserWorkspaceAssignments.includes(clientId)) {
        return "You do not have access to this client";
    }

    return null;
}

export function validateCreateClient(actingUserRole: OrganizationUserRole) {
    if (
        !hasAtLeastOrganizationRole(
            actingUserRole,
            OrganizationUserRole.MANAGER,
        )
    ) {
        return "Cannot create client";
    }

    return null;
}

export const ORGANIZATION_BILLING_SETUP_REQUIRED_ERROR =
    "Ask your organization owner to set up billing before adding clients";

export function validateOrganizationHasBillingSetUp(
    isBillingSetUp: boolean,
): ValidateResult {
    if (!isBillingSetUp) {
        return ORGANIZATION_BILLING_SETUP_REQUIRED_ERROR;
    }

    return null;
}

export const ORGANIZATION_BILLING_CANCELLED_ERROR =
    "Subscription cancelled. Restart billing to add clients.";

export function validateOrganizationBillingNotCancelled(
    subscriptionStatus: SubscriptionStatus | null | undefined,
): ValidateResult {
    if (subscriptionStatus === SubscriptionStatus.CANCELLED) {
        return ORGANIZATION_BILLING_CANCELLED_ERROR;
    }

    return null;
}

export const ORGANIZATION_PLAN_NOT_ALLOWED_ERROR =
    "This plan is not available for your organization";

export const ORGANIZATION_PLAN_REQUIRES_ORGANIZATION_BILLING_ERROR =
    "This plan is only available when your organization owns the client's billing";

interface ValidateOrganizationAllowedPlanParams {
    allowedPlans: FLAT_RATE_PLANS[] | null | undefined;
    plan: FLAT_RATE_PLANS;
    isOrganizationBillingOwner: boolean;
}

export function validateOrganizationAllowedPlan({
    allowedPlans,
    plan,
    isOrganizationBillingOwner,
}: ValidateOrganizationAllowedPlanParams): ValidateResult {
    // Restricted plans (e.g. Read-only) are never granted by a null/empty
    // `allowedPlans`: staff must explicitly list them. Free / Basic / Plus keep
    // the permissive default below.
    if (isRestrictedOrganizationClientPlan(plan)) {
        if (!isOrganizationBillingOwner) {
            return ORGANIZATION_PLAN_REQUIRES_ORGANIZATION_BILLING_ERROR;
        }

        const isExplicitlyAllowed =
            allowedPlans != null &&
            allowedPlans.length > 0 &&
            allowedPlans.includes(plan);
        return isExplicitlyAllowed ? null : ORGANIZATION_PLAN_NOT_ALLOWED_ERROR;
    }

    if (plan === FLAT_RATE_PLANS.ADVANCED) {
        return null;
    }

    if (allowedPlans == null || allowedPlans.length === 0) {
        return null;
    }

    if (!allowedPlans.includes(plan)) {
        return ORGANIZATION_PLAN_NOT_ALLOWED_ERROR;
    }

    return null;
}

export const TAX_PLAN_CHANGE_NOT_ALLOWED_ERROR =
    "Clients can't be moved onto the Tax plan. Contact support to change it.";

export const TAX_PLAN_CHANGE_TARGET_NOT_ALLOWED_ERROR =
    "Clients on the Tax plan can only be moved to Basic, Plus or Advanced.";

interface ValidateClientPlanChangeParams {
    currentPlan: FLAT_RATE_PLANS | null | undefined;
    plan: FLAT_RATE_PLANS;
}

/**
 * The Tax plan is set manually by staff: it can't be moved onto or off via the
 * client plan change flows, except from Tax to Basic / Plus / Advanced.
 */
export function validateClientPlanChange({
    currentPlan,
    plan,
}: ValidateClientPlanChangeParams): ValidateResult {
    if (currentPlan === plan) {
        return null;
    }

    if (plan === FLAT_RATE_PLANS.TAX) {
        return TAX_PLAN_CHANGE_NOT_ALLOWED_ERROR;
    }

    if (
        currentPlan === FLAT_RATE_PLANS.TAX &&
        !isTaxPlanChangeTargetPlan(plan)
    ) {
        return TAX_PLAN_CHANGE_TARGET_NOT_ALLOWED_ERROR;
    }

    return null;
}

export const TAX_PLAN_CLIENT_DELETE_NOT_ALLOWED_ERROR =
    "This client is on the Tax plan and can't be deleted. Contact support to delete it.";

/**
 * Tax clients are managed by staff, so an organization can't delete them.
 */
export function validateDeleteClientPlan(
    plan: FLAT_RATE_PLANS | null,
): ValidateResult {
    if (plan === FLAT_RATE_PLANS.TAX) {
        return TAX_PLAN_CLIENT_DELETE_NOT_ALLOWED_ERROR;
    }

    return null;
}

export const CLIENT_BILLING_TRANSFER_PLAN_NOT_ALLOWED_ERROR =
    "Billing for clients on this plan can't be transferred. Update the client's plan first.";

export function validateClientBillingTransferPlan(
    plan: FLAT_RATE_PLANS | null,
): ValidateResult {
    if (isClientBillingTransferBlockedPlan(plan)) {
        return CLIENT_BILLING_TRANSFER_PLAN_NOT_ALLOWED_ERROR;
    }

    return null;
}

export function validateManageOrganizationSettings(
    actingUserRole: OrganizationUserRole,
): ValidateResult {
    if (
        !hasAtLeastOrganizationRole(actingUserRole, OrganizationUserRole.ADMIN)
    ) {
        return "Cannot manage organization settings";
    }

    return null;
}

export function validateManageChartOfAccountsTemplates(
    actingUserRole: OrganizationUserRole,
): ValidateResult {
    if (
        !hasAtLeastOrganizationRole(
            actingUserRole,
            OrganizationUserRole.MANAGER,
        )
    ) {
        return "Cannot manage chart of accounts templates";
    }

    return null;
}

export function validateManageOrganizationReportSettings(
    actingUserRole: OrganizationUserRole,
): ValidateResult {
    if (
        !hasAtLeastOrganizationRole(
            actingUserRole,
            OrganizationUserRole.MANAGER,
        )
    ) {
        return "Cannot manage organization report settings";
    }

    return null;
}

export function validateManageAgentSkills(
    actingUserRole: OrganizationUserRole,
): ValidateResult {
    if (
        !hasAtLeastOrganizationRole(
            actingUserRole,
            OrganizationUserRole.MANAGER,
        )
    ) {
        return "Cannot manage agent skills";
    }

    return null;
}

interface ValidateManageTeamParams {
    actingUser: {
        id: number;
        role: OrganizationUserRole;
        workspaceAssignments: string[];
    };
    managedUser: {
        id: number;
        role: OrganizationUserRole;
        workspaceAssignments: string[];
    };
}

export function validateUpdateTeamMember({
    actingUser,
    managedUser,
}: ValidateManageTeamParams): ValidateResult {
    if (actingUser.id === managedUser.id) {
        return "Cannot manage your own assignment";
    }

    return (
        validateManageTeamRole(actingUser.role, managedUser.role) ??
        validateManageClientAssignment(
            actingUser.workspaceAssignments,
            managedUser.workspaceAssignments,
        )
    );
}

interface ValidateCreateInviteParams {
    actingUser: {
        role: OrganizationUserRole;
        workspaceAssignments: string[];
    };
    invite: {
        organizationRole: OrganizationUserRole;
        workspaceAssignments: string[];
    };
}

export function validateCreateInvite({
    actingUser,
    invite,
}: ValidateCreateInviteParams): ValidateResult {
    // not possible to invite an owner
    if (invite.organizationRole === OrganizationUserRole.OWNER) {
        return "Cannot invite an owner";
    }

    // members cannot invite anyone
    if (actingUser.role === OrganizationUserRole.MEMBER) {
        return "Members cannot invite anyone";
    }

    // owners and admins can invite anyone
    if (
        hasAtLeastOrganizationRole(actingUser.role, OrganizationUserRole.ADMIN)
    ) {
        return null;
    }

    // manager

    return validateManageClientAssignment(
        actingUser.workspaceAssignments,
        invite.workspaceAssignments,
    );
}

export function validateResendInvite(
    actingUserRole: OrganizationUserRole,
): ValidateResult {
    if (
        !hasAtLeastOrganizationRole(
            actingUserRole,
            OrganizationUserRole.MANAGER,
        )
    ) {
        return "Cannot resend invite";
    }

    return null;
}

interface ValidateCancelInviteParams {
    actingUser: {
        role: OrganizationUserRole;
        workspaceAssignments: string[];
    };
    invite: {
        organizationRole: OrganizationUserRole;
        workspaceAssignments: string[];
    };
}

export function validateCancelInvite({
    actingUser,
    invite,
}: ValidateCancelInviteParams): ValidateResult {
    return (
        validateManageTeamRole(actingUser.role, invite.organizationRole) ??
        validateManageClientAssignment(
            actingUser.workspaceAssignments,
            invite.workspaceAssignments,
        )
    );
}

export function validateManageTeamRole(
    actingUserRole: OrganizationUserRole,
    managedUserRole: OrganizationUserRole,
): ValidateResult {
    if (
        !hasAtLeastOrganizationRole(
            actingUserRole,
            OrganizationUserRole.MANAGER,
        )
    ) {
        return "Cannot manage team";
    }

    if (!hasAtLeastOrganizationRole(actingUserRole, managedUserRole)) {
        return "Cannot manage team with a higher role";
    }

    return null;
}

export function validateManageClientAssignment(
    actingUserWorkspaceAssignments: string[],
    managedUserWorkspaceAssignments: string[],
): ValidateResult {
    const hasAccessToAllWorkspaces = managedUserWorkspaceAssignments.every(
        (workspaceAssignment) =>
            actingUserWorkspaceAssignments.includes(workspaceAssignment),
    );

    if (!hasAccessToAllWorkspaces) {
        return "Cannot manage team with access to workspaces you don't have access to";
    }

    return null;
}
